import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import ConversationItem from './ConversationItem'
import ConversationPreloader from './ConversationPreloader'
import Checkbox from './Checkbox'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { useMinSkeletonDuration } from '../skeleton/useMinSkeletonDuration'
import { inboxContentReveal } from '../skeleton/inboxSkeletonStyles'
import { useConversationTagPolling } from './useConversationTagPolling'

const SCROLL_LOAD_THRESHOLD_PX = 80

const ConversationsList: React.FC = () => {
    const listRef = useRef<HTMLDivElement>(null)
    const [page, setPage] = useState(2)
    const { store: inboxStore, dispatch: inboxDispatch } = useInboxStore()
    const {
        fetchingConversations,
        fetchingMoreConversations,
        conversations,
        filteredConversations,
        selectedConversations,
        allConversationsFetched,
        pinned, starred, readed, tags, archived, query,
        lastMessagedFrom, lastMessagedTo, conversationStatus, lastMessagedOnlyLast, conversationOwners,
        selectedTab,
        activeInboxLabel,
    } = inboxStore

    const {
        getConversationsFirstPage,
        getFilteredLDConversations,
        shouldFetchConversationsFromLinkedin,
        loadMoreConversations,
        loadMoreConversationsFromLinkedin,
        loadMoreSalesNavigatorSearchConversations,
        loadMoreAllMessagesSearchConversations,
        salesNavigatorSearchActive,
        allMessagesSearchActive,
        openConversation,
        filterConversations,
        visitLinkedInConversationHandler,
        setResetLastFetchedConversation,
        setResetSelectedConversations,
        snoozeConversationHandler,
    } = useInboxHandlers()

    useConversationTagPolling()

    const ownerFilterOn =
        Array.isArray(conversationOwners) && conversationOwners.some((o: any) => o?.id !== 'all')

    const getConversationsFromLinkedin = shouldFetchConversationsFromLinkedin()

    const filtersApplied = pinned !== 'All' || starred !== 'All' || tags.length !== 0 ||
        archived !== 'No' || readed !== 'All' || query.length > 0 ||
        lastMessagedFrom !== '' || lastMessagedTo !== '' || conversationStatus?.value !== 'all' ||
        lastMessagedOnlyLast || ownerFilterOn || activeInboxLabel !== 'allMessages'

    /** Label/sidebar filters: only page 1 on tab change; page 2+ on scroll (avoids instant double paginate). */
    const autoLoadMoreOnUnderflow =
        activeInboxLabel === 'allMessages' || activeInboxLabel === 'mainInbox'

    /**
     * LinkedIn/SN search + read/archived LI fetch — same branch as linkedinextension.
     * Label tabs, tags, pinned, etc. still use `getConversationsFirstPage` (auto-select first row, extension bootstrap).
     */
    const needsLinkedInFilteredFetch = query.trim().length > 0 || getConversationsFromLinkedin

    /** Paginate search/read-archived via LI cursor, not DB page counter (query is ignored by paginate API). */
    const loadMoreViaLinkedIn = getConversationsFromLinkedin || query.trim().length > 0

    useEffect(() => {
        setResetLastFetchedConversation()
        if (needsLinkedInFilteredFetch) {
            getFilteredLDConversations(getConversationsFromLinkedin)
        } else {
            setPage(2)
            getConversationsFirstPage(activeInboxLabel)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pinned, starred, readed, archived, tags.length, lastMessagedFrom, lastMessagedTo, conversationStatus?.value, lastMessagedOnlyLast, conversationOwners, query, selectedTab, activeInboxLabel])

    useEffect(() => {
        filterConversations(conversations)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversations, query, selectedTab, activeInboxLabel])

    useEffect(() => {
        if (allConversationsFetched) setPage(2)
    }, [allConversationsFetched])

    useEffect(() => {
        setResetSelectedConversations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, pinned, starred, readed, archived, tags.length, activeInboxLabel])

    // Auto-load next page when current content doesn't overflow the container
    // (handles case where first page has too few items to enable scrolling).
    // Disabled for label tabs — otherwise page 2 fires immediately after page 1 on tab switch.
    useEffect(() => {
        if (!autoLoadMoreOnUnderflow) return
        if (fetchingConversations || fetchingMoreConversations || allConversationsFetched) return
        const list = listRef.current
        if (!list) return
        if (list.scrollHeight <= list.clientHeight) {
            loadMoreConversations(page).then(didLoad => {
                if (didLoad) setPage(p => p + 1)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoadMoreOnUnderflow, conversations.length, filteredConversations.length, fetchingConversations, fetchingMoreConversations, allConversationsFetched])

    const handleScroll = async ({ currentTarget }: React.UIEvent<HTMLDivElement>) => {
        const { scrollHeight, scrollTop, clientHeight } = currentTarget
        const distanceToBottom = scrollHeight - scrollTop - clientHeight
        const nearBottom = distanceToBottom <= SCROLL_LOAD_THRESHOLD_PX

        if (
            !allConversationsFetched &&
            !fetchingMoreConversations &&
            !fetchingConversations &&
            nearBottom
        ) {
            if (loadMoreViaLinkedIn) {
                if (allMessagesSearchActive) {
                    await loadMoreAllMessagesSearchConversations()
                } else if (selectedTab === 'SalesNavigator' && salesNavigatorSearchActive) {
                    await loadMoreSalesNavigatorSearchConversations()
                } else {
                    await loadMoreConversationsFromLinkedin()
                }
            } else {
                const didLoad = await loadMoreConversations(page)
                if (didLoad) setPage(p => p + 1)
            }
        }
    }

    const toggleConversationSelection = (conversation: any) => {
        const isSelected = selectedConversations.some((c: any) => c.conversationId === conversation.conversationId)
        const updated = isSelected
            ? selectedConversations.filter((c: any) => c.conversationId !== conversation.conversationId)
            : [...selectedConversations, { ...conversation, isSelected: true }]

        const mappedConversations = conversations.map((c: any) =>
            c.conversationId === conversation.conversationId ? { ...c, isSelected: !isSelected } : c
        )
        inboxDispatch({ type: 'set-conversations', payload: mappedConversations })
        inboxDispatch({ type: 'set-selected-conversations', payload: updated })
    }

    const resetSelectedConversations = () => {
        inboxDispatch({ type: 'set-conversations', payload: conversations.map((c: any) => ({ ...c, isSelected: false })) })
        inboxDispatch({ type: 'set-selected-conversations', payload: [] })
    }

    const preloaders = (count: number) => Array(count).fill(null).map((_, i) => <ConversationPreloader key={i} />)

    const displayList = filtersApplied ? (filteredConversations ?? []) : conversations
    const showListSkeleton = useMinSkeletonDuration(fetchingConversations)

    return (
        <Container>
            {selectedConversations?.length > 0 && (
                <SelectionBar>
                    <Checkbox checkboxState="ind" onClick={resetSelectedConversations} tooltip="Clear Selection" />
                    <SelectionCount>{selectedConversations.length} selected</SelectionCount>
                </SelectionBar>
            )}
            <List ref={listRef} onScroll={handleScroll}>
                {showListSkeleton ? (
                    preloaders(12)
                ) : displayList?.length ? (
                    <ListContent>
                        {displayList.map((conv: any) => (
                            <ConversationItem
                                key={conv.conversationId}
                                conversation={conv}
                                onOpen={openConversation}
                                onToggleSelect={toggleConversationSelection}
                                onVisitLinkedIn={visitLinkedInConversationHandler}
                                onSnooze={snoozeConversationHandler}
                            />
                        ))}
                        {fetchingMoreConversations && preloaders(2)}
                    </ListContent>
                ) : (
                    <NoResults>
                        <NoResultsHeading>No results</NoResultsHeading>
                        {query ? (
                            <NoResultsText>The term you entered did not bring any results.</NoResultsText>
                        ) : (
                            <NoResultsText>No conversations found for this combination of filters.</NoResultsText>
                        )}
                    </NoResults>
                )}
            </List>
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 0 12px 12px;
`
const SelectionBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0 8px;
    flex-shrink: 0;
`
const SelectionCount = styled.span`
    font-size: 12px;
    font-weight: bold;
    color: #5964ff;
`
/**
 * The scrollable element. We use the `flex: 1 1 auto; height: 0` pattern (same as the
 * working LinkedIn extension inbox) so that the element is forced to fill remaining
 * flex space *and* is a definite scroll container — i.e. its content overflow makes
 * it scroll instead of bubbling up to a parent. Previously this used `flex: 1` plus
 * `overflow-x: visible`, which in some browsers degenerates to `overflow-x: auto` and
 * also lets the parent become the actual scroller, which silently broke `onScroll`.
 */
const List = styled.div`
    width: 100%;
    flex: 1 1 auto;
    height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    padding-right: 4px;

    &::-webkit-scrollbar { display: none; }
    scrollbar-width: none;
`
const ListContent = styled.div`
    ${inboxContentReveal}
`
const NoResults = styled.div`
    padding: 24px 16px;
`
const NoResultsHeading = styled.p`
    font-size: 14px;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
    margin-bottom: 4px;
`
const NoResultsText = styled.p`
    font-size: 13px;
    color: #545e77;
    line-height: 1.5;
`

export default ConversationsList
